import { api } from './authService';

export interface VeterinaryProduct {
  id: string;
  commercial_name: string;
  license_holder: string;
  authorization_number?: string;
  description?: string;
  pharmaceutical_form?: string;
  prescription_mode: 'Cu prescripție' | 'Fără prescripție';
  atc_code?: string;
  concentrations?: any;
  category: 'general' | 'antiparazitar_extern' | 'vaccin' | 'antibiotic' | 'analgezic' | 'anestezie';
  is_active: boolean;
  authorization_date?: string;
  expiration_date?: string;
  source_file?: string;
  row_number?: number;
  metadata?: any;
  active_substances: ActiveSubstance[];
  target_species: TargetSpecies[];
  created_at: string;
  updated_at: string;
}

export interface ActiveSubstance {
  id: string;
  name: string;
  description?: string;
  cas_number?: string;
  molecular_formula?: string;
  concentration?: string;
  order?: number;
}

export interface TargetSpecies {
  id: string;
  name: string;
  scientific_name?: string;
  description?: string;
}

export interface VetProductFilters {
  page?: number;
  limit?: number;
  search?: string;
  species?: string;
  active_substance?: string;
  pharmaceutical_form?: string;
  prescription_mode?: 'Cu prescripție' | 'Fără prescripție';
  category?: string;
  license_holder?: string;
}

export interface VetProductFilterOptions {
  species: Array<{ id: string; name: string; scientific_name?: string }>;
  active_substances: Array<{ id: string; name: string }>;
  pharmaceutical_forms: string[];
  categories: string[];
  license_holders: string[];
}

class VetProductsService {
  private compactFilters(filters: VetProductFilters): Record<string, unknown> {
    const entries = Object.entries(filters).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      return true;
    });
    return Object.fromEntries(entries);
  }

  /**
   * Obține lista de produse medicinale cu filtrare și paginare
   */
  async getProducts(filters: VetProductFilters = {}): Promise<{
    data: VeterinaryProduct[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const response = await api.get('/vet-products', {
        params: this.compactFilters(filters),
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching vet products:', error);
      throw new Error(error.response?.data?.message || 'Eroare la încărcarea produselor');
    }
  }

  /**
   * Obține detalii complete pentru un produs
   */
  async getProductById(id: string): Promise<VeterinaryProduct> {
    try {
      const response = await api.get(`/vet-products/${id}`);
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching vet product:', error);
      throw new Error(error.response?.data?.message || 'Eroare la încărcarea produsului');
    }
  }

  /**
   * Căutare avansată cu multiple criterii
   */
  async advancedSearch(params: {
    q?: string;
    species?: string[];
    active_substances?: string[];
    forms?: string[];
  }): Promise<{ data: VeterinaryProduct[]; count: number }> {
    try {
      const response = await api.get('/vet-products/search/advanced', {
        params
      });
      return response.data;
    } catch (error: any) {
      console.error('Error in advanced search:', error);
      throw new Error(error.response?.data?.message || 'Eroare la căutare');
    }
  }

  /**
   * Obține opțiunile disponibile pentru filtre
   */
  async getFilterOptions(): Promise<VetProductFilterOptions> {
    try {
      const response = await api.get('/vet-products/filters/options');
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching filter options:', error);
      throw new Error(error.response?.data?.message || 'Eroare la încărcarea opțiunilor de filtrare');
    }
  }
}

const vetProductsService = new VetProductsService();
export default vetProductsService;

